import { MapPin, Satellite, CheckCircle2, Camera } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ReportHeroProps {
  address: string;
  satelliteUrl: string;
  streetViewUrl?: string;
  generatedAt: Date;
  confidence: number;
  source: string;
  edgeMethod?: string;
}

export function ReportHero({ address, satelliteUrl, streetViewUrl, generatedAt, confidence, source, edgeMethod = "Geometric" }: ReportHeroProps) {
  const conf = Math.round(confidence);
  const tier = conf >= 80 ? "High" : conf >= 60 ? "Medium" : "Low";
  const tierColor = conf >= 80 ? "text-primary" : conf >= 60 ? "text-accent-foreground" : "text-destructive";
  const confidenceBarClass = conf >= 80 ? "bg-primary" : conf >= 60 ? "bg-accent" : "bg-destructive";

  return (
    <div className="space-y-4">
      <div className="relative h-[240px] overflow-hidden rounded-2xl border border-border bg-muted sm:h-[280px]">
        <img src={satelliteUrl} alt={`Satellite view of ${address}`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-xl font-bold text-white drop-shadow-lg sm:text-2xl">
              <MapPin className="w-5 h-5" /> Roof Measurement Report
            </h2>
            <p className="text-sm text-white/90 drop-shadow break-words">{address}</p>
          </div>
          <Badge className="w-fit bg-primary text-primary-foreground gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Satellite Verified
          </Badge>
        </div>
      </div>

      <div className="flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <span className="break-words">Generated: {generatedAt.toLocaleString()}</span>
        <span className="flex items-center gap-1"><Satellite className="w-3 h-3" /> {source}</span>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary" /> Measurement Confidence
          </span>
          <span className={`text-sm font-bold ${tierColor}`}>{conf}/100 — {tier}</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className={`h-full transition-all ${confidenceBarClass}`} style={{ width: `${conf}%` }} />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Badge variant="outline" className="text-xs">{source}</Badge>
          <span className="text-xs text-muted-foreground break-words">Edge method: {edgeMethod}</span>
        </div>
      </div>

      {streetViewUrl && (
        <div className="rounded-xl border border-border overflow-hidden bg-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-medium flex items-center gap-2">
              <Camera className="w-4 h-4 text-primary" /> Street View
            </span>
            <span className="text-xs text-muted-foreground">Property photo</span>
          </div>
          <img
            src={streetViewUrl}
            alt={`Street view of ${address}`}
            className="w-full aspect-[16/10] object-cover"
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
}
